import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useMountedState } from 'react-use'
import { createContainer } from 'unstated-next'

import {
  CartLineInput,
  CartLineUpdateInput,
  CustomAttribute,
  CustomAttributeInput,
  GetCartVariantsQuery,
} from '../../../__generated__/graphql/catalog/graphql'
import {
  reportClientError,
  reportClientEvent,
} from '../../../app/_components/chrome/scripts/DataDogRumScript'
import { cookieKeys } from '../../../app/_config/Cookies.config'
import { useCookies } from '../../../app/_providers/CookiesProvider.client'
import { useGraphqlClients } from '../../../app/_providers/GraphqlClientsProvider.client'
import { useLocalization } from '../../../app/_providers/LocalizationProvider.client'
import {
  useTrackCartModified,
  useTrackProceedToCheckout,
} from '../../../app/_providers/TrackingProvider.client'
import { useTranslation } from '../../../app/_providers/TranslationProvider.client'
import { useCartStore } from '../../../lib/adapters/cart-persistence/useCartStore'
import { mapCartItemToGtmVariant } from '../../../lib/analytics/analytics'
import { mapPropertiesToCustomAttributes } from '../../../lib/cart-client/utils'
import { FETCH_CART_VARIANTS } from '../../../lib/graphql/queries/FETCH_CART_VARIANTS'
import { QueryStatus } from '../../../lib/hooks/types'
import { GtmVariant, useGoogleTagManager } from '../../../lib/hooks/useGoogleTagManager'
import {
  deriveMapFromList,
  isEmbroiderableProduct,
  isEmbroideryItem,
  isLogoEmbroiderySetup,
} from '../../../lib/utils'
import { Cart, CartItem } from '../../../types/figs'

import { areCartLinesEqual, augmentPersistedCartItemsWithFigsData } from './utils'

export interface UseCartNewCartItem {
  finalSale?: boolean
  item: GtmVariant
  properties?: Record<string, string | undefined>
  quantity?: number
  swPromoEligible?: boolean | undefined
  isPortalColor?: boolean
  isPortalLogo?: boolean
}

export interface UseCartOutput {
  cart: Omit<Cart, 'id'>
  addItems(itemsToAdd: UseCartNewCartItem[]): void
  addGlobalAttributes(attributes: CustomAttribute[]): void
  removeItems(keys: string[], options?: { removeAssociatedPortalProduct: boolean }): void
  updateItemsQuantity(
    keys: string[],
    calculateQuantity: ((currentQuantity: number) => number) | number
  ): void
  proceedToCheckout(props?: {
    callbackBeforeRedirect?: () => Promise<void>
    discountCode?: string
  }): Promise<void>
  status: Exclude<QueryStatus, 'idle'>
  lastAddedItems: Array<{
    sku: string
    properties: Record<string, string | undefined>
  }>
  errorMessage: string | undefined
}

// TODO: update types to no longer reference `LocalCart`. Slim down types as well.
function useCart(): UseCartOutput {
  const [cookies] = useCookies([cookieKeys.discountCode.key, cookieKeys.facebook.key])
  const discountCookieVal = cookies[cookieKeys.discountCode.key]
  const facebookCookieVal = cookies[cookieKeys.facebook.key]
  const checkIsMounted = useMountedState()
  const { trackProceedToCheckout } = useTrackProceedToCheckout()
  const { pushAddToCart: pushAddToCartToGtm, pushRemoveFromCart: pushRemoveFromCartToGtm } =
    useGoogleTagManager()
  const { trackCartModified } = useTrackCartModified()
  const { region, locale } = useLocalization()
  const {
    cart: { finalSale, errorMessages },
  } = useTranslation()
  const { cacheOptimizedClient: figsPublicClient } = useGraphqlClients()
  const [lastAddedItems, setLastAddedItems] = useState<UseCartOutput['lastAddedItems']>([])
  const [errorMessage, setErrorMessage] = useState<string>()

  const {
    status: cartStoreStatus,
    cart: cartStoreCart,
    addItemsToCart: addItemsToCartWithoutEvent,
    removeItemsFromCart: removeItemsFromCartWithoutEvent,
    updateItemsInCart: updateItemsInCartWithoutEvent,
    addCartAttributes,
    retrieveCheckoutUrl,
  } = useCartStore()

  const uniqueCartSkus = useMemo(() => {
    return [...new Set<string>(cartStoreCart.lines.map(i => i.sku).filter(s => s !== null))]
  }, [cartStoreCart.lines])

  // NOTE: It doesn't make sense to "augment" data at the level of the cart provider...
  // we don't know yet what data people need. At most we should just be exposing ids for
  // cart items and letting different UIs decide what they want to query based on what they
  // need to display. They can also do pagination then... whereas here we end up calling 100s
  // of queries if we have 100s of cart items. Even if a user never once even looks inside the cart
  // at those items. It is wasteful and creates coupling of data types.
  const [variantsStatus, setVariantsStatus] = useState<UseCartOutput['status']>('pending')
  const [variants, setVariants] = useState<
    NonNullable<GetCartVariantsQuery['productCatalogVariants']>
  >([])

  // TODO: we don't really want to do this here. Each respective UI should be loading this data.
  // TODO: update types so we aren't returning the desired fields, then add the query as needed.
  const previousFetchedVariantSkus = useRef<string[]>([])
  if (previousFetchedVariantSkus.current !== uniqueCartSkus && checkIsMounted()) {
    previousFetchedVariantSkus.current = uniqueCartSkus
    setVariantsStatus('pending')
    const newVariants: typeof variants = []
    const loadVariantDataForSkus = async () => {
      let variantFailed = false
      const variantQueries = uniqueCartSkus.map(sku => {
        return figsPublicClient.query({
          query: FETCH_CART_VARIANTS,
          variables: { skus: [sku] },
          context: {
            includeUnavailable: true, // We should fetch data for anything the cart API tells us is in the cart. So turning off filtering.
          },
        })
      })
      const variantResponses = await Promise.allSettled(variantQueries)

      variantResponses.forEach(variantResponse => {
        if (variantResponse.status === 'fulfilled') {
          newVariants.push(...(variantResponse.value.data?.productCatalogVariants ?? []))
        } else {
          variantFailed = true
        }
      })
      setVariants(newVariants)
      if (variantFailed) {
        setVariantsStatus('rejected')
      } else {
        setVariantsStatus('resolved')
      }
    }

    loadVariantDataForSkus()
  }

  const cart = useMemo<Omit<Cart, 'id'>>(() => {
    const variantsBySku = deriveMapFromList(variants, o => o.sku)

    const augmentedCartItems = augmentPersistedCartItemsWithFigsData(
      cartStoreCart.lines,
      variantsBySku,
      locale
    )

    // TODO: eventually use localized return values from cart-service
    const totalPrice: number = augmentedCartItems.reduce(
      (sum, item) => sum + (item.effectivePrice ?? 0) * item.quantity,
      0
    )

    return {
      items: deriveMapFromList(augmentedCartItems, o => o.key),
      itemCount: cartStoreCart.totalQuantity,
      totalPrice,
      attributes: cartStoreCart.attributes,
    }
  }, [variants, cartStoreCart.lines, cartStoreCart.totalQuantity, cartStoreCart.attributes, locale])

  const removeItemsFromCartStore = useCallback(
    async (itemsToRemove: CartItem[]) => {
      await removeItemsFromCartWithoutEvent(itemsToRemove.map(itemToRemove => itemToRemove.key))
      itemsToRemove.forEach(itemToRemove => {
        const variantToTrack = mapCartItemToGtmVariant(itemToRemove, region.currency)
        pushRemoveFromCartToGtm({ variant: variantToTrack, quantity: itemToRemove.quantity })
      })
    },
    [pushRemoveFromCartToGtm, region.currency, removeItemsFromCartWithoutEvent]
  )

  const addGlobalAttributesToCartStore = useCallback(
    async (attributes: CustomAttribute[]) => {
      await addCartAttributes(attributes)
    },
    [addCartAttributes]
  )

  const addItemsToCartStore = useCallback(
    async (incomingItems: UseCartNewCartItem[]) => {
      /**
       * Shopify combines variants into one line item with the quantity being
       * the sum of the individual variant quantities if:
       * 1. The variant IDs match
       * 2. The custom line item properties match
       *
       * We have one attribute, `_collectionAttribution`, that should be updated
       * instead of being treated as unique. To manage that this behavior, we must
       * identify a matching line item and perform an update instead of an add
       * (if applicable).
       */
      const itemsToAdd: CartLineInput[] = []
      const itemsToUpdate: CartLineUpdateInput[] = []
      incomingItems.forEach(item => {
        const formattedItem = {
          attributes: Object.entries(item.properties ?? {})
            .filter((e): e is [string, string] => typeof e[1] === 'string' && e[1] !== '')
            .map(([key, value]) => ({
              key: key,
              value: value,
            })),
          quantity: item.quantity ?? 1,
          sku: item.item.sku,
        }
        const existingMatchingLineItem = Object.values(cart.items).find(e => {
          const formattedElement = {
            attributes: mapPropertiesToCustomAttributes(e.properties),
            quantity: e.quantity,
            sku: e.sku,
          }
          return areCartLinesEqual(formattedItem, formattedElement)
        })
        if (existingMatchingLineItem) {
          if (item.properties?._collectionAttribution) {
            // if the `_collectionAttribution` prop is present, we must merge the values into one prop,
            // then update the existing line item.
            const collectionAttributionsSet = existingMatchingLineItem.properties
              ._collectionAttribution
              ? new Set<string>(
                  existingMatchingLineItem.properties._collectionAttribution.split(',')
                )
              : new Set<string>()
            collectionAttributionsSet.add(item.properties._collectionAttribution)
            const updatedCollectionAttributions = Array.from(collectionAttributionsSet).join(',')
            const newAttributes = mapPropertiesToCustomAttributes(
              existingMatchingLineItem.properties
            )
            const replacementIndex = newAttributes.findIndex(
              e => e.key === '_collectionAttribution'
            )
            if (replacementIndex >= 0) {
              newAttributes[replacementIndex] = {
                key: '_collectionAttribution',
                value: updatedCollectionAttributions,
              }
            } else {
              newAttributes.push({
                key: '_collectionAttribution',
                value: updatedCollectionAttributions,
              })
            }
            itemsToUpdate.push({
              id: existingMatchingLineItem.key,
              attributes: newAttributes,
              quantity: existingMatchingLineItem.quantity + (item.quantity ?? 1),
            })
          } else {
            // we should simply update the existing item's quantity
            itemsToUpdate.push({
              id: existingMatchingLineItem.key,
              attributes: mapPropertiesToCustomAttributes(existingMatchingLineItem.properties),
              quantity: existingMatchingLineItem.quantity + (item.quantity ?? 1),
            })
          }
        } else {
          // this item needs to be added
          const attributes: CustomAttributeInput[] = mapPropertiesToCustomAttributes(
            item.properties ?? {}
          )

          if (item?.properties?._kitId) {
            attributes.push({ key: '_setUid', value: item.properties._kitId + '1' })
          }
          if (item.finalSale) {
            attributes.push({ key: '_LPROP', value: 'final sale' })
          }
          if (item.isPortalColor) {
            attributes.push({ key: '_isPortalColor', value: 'true' })
          }
          if (item.isPortalLogo) {
            attributes.push({ key: '_isPortalLogo', value: 'true' })
          }

          itemsToAdd.push({
            merchandiseId: item.item.shopifyId,
            quantity: item.quantity ?? 1,
            attributes: attributes.length > 0 ? attributes : undefined,
          })
        }
      })

      if (itemsToAdd.length > 0) {
        await addItemsToCartWithoutEvent(itemsToAdd)
      }
      if (itemsToUpdate.length > 0) {
        await updateItemsInCartWithoutEvent(itemsToUpdate)
      }

      incomingItems.forEach(item => {
        pushAddToCartToGtm({ variant: item.item })
      })
    },
    [addItemsToCartWithoutEvent, cart.items, pushAddToCartToGtm, updateItemsInCartWithoutEvent]
  )

  const setItemQuantitiesInCartStore = useCallback(
    async (itemsToUpdate: Array<{ item: CartItem; newQuantity: number }>) => {
      itemsToUpdate.forEach(({ item, newQuantity }) => {
        const oldQuantity = item.quantity
        const variantToTrack = mapCartItemToGtmVariant(item, region.currency)
        if (newQuantity > oldQuantity) {
          pushAddToCartToGtm({
            variant: variantToTrack,
            quantity: newQuantity - oldQuantity,
          })
        } else {
          pushRemoveFromCartToGtm({
            variant: variantToTrack,
            quantity: oldQuantity - newQuantity,
          })
        }
      })

      const convertedItemsToUpdate: CartLineUpdateInput[] = itemsToUpdate.map(
        ({ item, newQuantity }) => ({
          id: item.cartStoreKey ?? item.key,
          quantity: newQuantity,
          attributes: mapPropertiesToCustomAttributes(item.properties),
        })
      )

      await updateItemsInCartWithoutEvent(convertedItemsToUpdate)
    },
    [pushAddToCartToGtm, pushRemoveFromCartToGtm, region.currency, updateItemsInCartWithoutEvent]
  )

  const status = useMemo<UseCartOutput['status']>(() => {
    if (cartStoreStatus === 'pending' || variantsStatus === 'pending') return 'pending'
    if (variantsStatus === 'rejected' || cartStoreStatus === 'rejected') return 'rejected'
    return 'resolved'
  }, [cartStoreStatus, variantsStatus])

  useEffect(() => {
    // if we are still loading data or we haven't obtained a cart yet, skip signaling
    if (status !== 'resolved' || cartStoreCart.id === null) return
    const signalCartItems = Object.values(cart.items).map(item => {
      const signalCartItem: Parameters<typeof trackCartModified>[0]['items'][number] = {
        title: item.title,
        quantity: item.quantity,
        variantSku: item.sku,
        variantShopifyId: item.variantId,
        effectivePrice: item.effectivePrice,
        productShopifyId: item.shopifyProductId,
      }
      return signalCartItem
    })
    trackCartModified({
      id: cartStoreCart.id,
      items: signalCartItems,
    })
  }, [cartStoreCart.id, cart.items, trackCartModified, status])

  const aggregateThisAndRelatedEmbroideryItems = useCallback(
    (guid: string | undefined, cartItems: Record<string, CartItem>): CartItem[] => {
      return Object.values(cartItems).filter(item => item.properties._guid === guid)
    },
    []
  )

  const aggregateThisAndRelatedPendingEmbroideryLogos = useCallback(
    (logoId: string | undefined, cartItems: Record<string, CartItem>) => {
      return Object.values(cartItems).filter(item => item.properties._logo_id === logoId)
    },
    []
  )

  const aggregateRestOfKitsNotRelatedToEmbroideryItems = useCallback(
    (kitId: string | undefined, cartItems: Record<string, CartItem>): CartItem[] => {
      return Object.values(cartItems).filter(
        item => item.properties._kitId === kitId && !item.properties._guid
      )
    },
    []
  )

  /**
   * When removing an embroiderable product or logo embroidery we need to check
   * if the associated logo embroidery is the last one in the cart. If it is we
   * need to remove the logo setup item as well.  We check if there are 3 items
   * that include the logo id one of which is the logo setup item, the other
   * two being the embroiderable product and the associated logo embroidery item.
   * In this case we return the logo setup item to be removed.
   */
  const getLogoSetupItem = useCallback(
    (logoId: string | undefined, cartItems: Record<string, CartItem>) => {
      const otherProductsThatHavePendingLogoSetup = Object.values(cartItems).filter(item => {
        return item.properties._logo_id === logoId
      })
      if (otherProductsThatHavePendingLogoSetup.length === 3) {
        const logoSetupItem = otherProductsThatHavePendingLogoSetup.find(
          item => item.properties._is_logo_setup === 'true'
        )
        if (logoSetupItem) {
          return logoSetupItem
        }
      }
      return
    },
    []
  )

  const getNewKitId = useCallback(
    (oldKitId: string | undefined, cartItems: Record<string, CartItem>): string => {
      return (
        Object.values(cartItems)
          .filter(item => item.productType !== 'Embroidery' && item.properties._kitId === oldKitId)
          .map(o => o.sku)
          .sort()
          .join() + ','
      )
    },
    []
  )

  const addGlobalAttributes = useCallback<UseCartOutput['addGlobalAttributes']>(
    async attributes => {
      await addGlobalAttributesToCartStore(attributes)
    },
    [addGlobalAttributesToCartStore]
  )

  const addItems = useCallback<UseCartOutput['addItems']>(
    async (itemsToAdd): Promise<void> => {
      try {
        await addItemsToCartStore(itemsToAdd)
        // TODO: depending on business needs for `lastAddedItems`,
        // consider filtering our embroidery items here.
        setLastAddedItems(
          itemsToAdd.map(e => ({ sku: e.item.sku, properties: { ...e.properties } }))
        )
        setErrorMessage(undefined)
      } catch {
        // if error, reset `lastAddedSkus` to prevent past success values from bleeding through
        setLastAddedItems([])
        setErrorMessage(errorMessages.addLine)
      }
    },
    [addItemsToCartStore, errorMessages.addLine]
  )

  const removeItems = useCallback<UseCartOutput['removeItems']>(
    async (keys, options): Promise<void> => {
      const itemsToRemove: Map<string, CartItem> = new Map<string, CartItem>()
      const itemsToAddBack: CartLineInput[] = []

      keys.forEach(key => {
        const itemToRemove = cart.items[key]
        if (!itemToRemove) return

        // FIXME: this logic has the ability to over-add items to `itemsToRemove`.
        // The `Map` implementation prevents this from becoming an issue, but we should
        // look to make this logic more concise and predictable for different
        // embroidery removal scenarios.
        if (isEmbroiderableProduct(itemToRemove)) {
          const thisAndRelatedEmbroideryItems = aggregateThisAndRelatedEmbroideryItems(
            itemToRemove.properties._guid,
            cart.items
          )

          thisAndRelatedEmbroideryItems.forEach(item => {
            itemsToRemove.set(item.key, item)
          })

          if (itemToRemove.properties._logo_id) {
            const thisAndRelatedLogoEmbroideryItems = aggregateThisAndRelatedPendingEmbroideryLogos(
              itemToRemove.properties._logo_id,
              cart.items
            )

            thisAndRelatedLogoEmbroideryItems.forEach(item => {
              itemsToRemove.set(item.key, item)
            })

            // Find items with different _guid that are Scrub Tops
            const scrubTopsToAddBack = thisAndRelatedLogoEmbroideryItems
              .filter(
                item =>
                  item.productType === 'Scrub Top' &&
                  item.properties._guid !== itemToRemove.properties._guid
              )
              .map(item => {
                // Remove embroidery-specific properties
                const {
                  _embroideryId,
                  _guid,
                  _logo_id,
                  _LPROP,
                  [finalSale]: FinalSale,
                  ...rest
                } = item.properties
                if (item.finalSale) {
                  rest._LPROP = _LPROP
                  rest[finalSale] = FinalSale
                }
                return {
                  ...item,
                  properties: rest,
                }
              })

            if (scrubTopsToAddBack.length > 0) {
              const convertedScrubTopsToAddBack: CartLineInput[] = scrubTopsToAddBack.map(s => ({
                merchandiseId: s.variantId,
                quantity: s.quantity,
                attributes: mapPropertiesToCustomAttributes(s.properties),
              }))
              itemsToAddBack.push(...convertedScrubTopsToAddBack)
            }

            const logoSetup = getLogoSetupItem(itemToRemove.properties._logo_id, cart.items)
            if (logoSetup) {
              itemsToRemove.set(logoSetup.key, logoSetup)
            }
          }
        } else if (isEmbroideryItem(itemToRemove)) {
          /**
           * We remove this and all related embroiderable items and re-add only
           * the related embroiderable items with the embroidery-specific line
           * item properties removed. This way variants can be combined with
           * matching variants with the same custom line item properties, which
           * is how Shopify distinguishes unique line items.
           * */

          let thisAndRelatedEmbroiderableItems: CartItem[]
          if (isLogoEmbroiderySetup(itemToRemove)) {
            thisAndRelatedEmbroiderableItems = aggregateThisAndRelatedPendingEmbroideryLogos(
              itemToRemove.properties._logo_id,
              cart.items
            )
          } else {
            thisAndRelatedEmbroiderableItems = aggregateThisAndRelatedEmbroideryItems(
              itemToRemove.properties._guid,
              cart.items
            )
          }

          const relatedEmbroiderableItems = thisAndRelatedEmbroiderableItems
            .filter(isEmbroiderableProduct)
            .map(item => {
              const {
                _embroideryId,
                _guid,
                _LPROP,
                [finalSale]: FinalSale,
                _kitId,
                _setUid,
                _logo_id,
                ...rest
              } = item.properties
              if (_kitId) {
                const newKitId = getNewKitId(_kitId, cart.items)
                rest._kitId = newKitId
                rest._setUid = newKitId + '1'
              }
              if (item.finalSale) {
                rest._LPROP = _LPROP
                rest[finalSale] = FinalSale
              }
              return {
                ...item,
                properties: rest,
              }
            })

          if (itemToRemove.properties._kitId) {
            const restOfKitGroup = aggregateRestOfKitsNotRelatedToEmbroideryItems(
              itemToRemove.properties._kitId,
              cart.items
            )
            restOfKitGroup.forEach(item => {
              const { _kitId, _setUid, ...rest } = item.properties
              const newKitId = getNewKitId(_kitId, cart.items)
              rest._kitId = newKitId
              rest._setUid = newKitId + '1'
              relatedEmbroiderableItems.push({
                ...item,
                properties: rest,
              })
              thisAndRelatedEmbroiderableItems.push(item)
            })
          }

          if (itemToRemove.properties._logo_id) {
            const logoSetup = getLogoSetupItem(itemToRemove.properties._logo_id, cart.items)
            if (logoSetup) {
              thisAndRelatedEmbroiderableItems.push(logoSetup)
            }
          }

          thisAndRelatedEmbroiderableItems.forEach(item => {
            itemsToRemove.set(item.key, item)
          })

          const hasAssociatedPortalProduct = thisAndRelatedEmbroiderableItems.some(
            item => item.properties['_isPortalColor'] === 'true'
          )
          // Avoid adding back associated embroidered products for portals that enforce text/logo embroidery
          if (!(options?.removeAssociatedPortalProduct && hasAssociatedPortalProduct)) {
            const convertedRelatedEmbroiderableItems: CartLineInput[] =
              relatedEmbroiderableItems.map(e => ({
                merchandiseId: e.variantId,
                quantity: e.quantity,
                attributes: mapPropertiesToCustomAttributes(e.properties),
              }))
            itemsToAddBack.push(...convertedRelatedEmbroiderableItems)
          }
        } else {
          itemsToRemove.set(itemToRemove.key, itemToRemove)
        }
      })

      if (itemsToRemove.size > 0) {
        try {
          await removeItemsFromCartStore([...itemsToRemove.values()])
          setErrorMessage(undefined)
        } catch {
          setErrorMessage(errorMessages.removeLine)
        }
      }
      if (itemsToAddBack.length > 0) {
        try {
          await addItemsToCartWithoutEvent(itemsToAddBack)
        } catch {
          // if we have errors here, we should just ignore them; consider it an imperfect removal.
        }
      }
    },
    [
      addItemsToCartWithoutEvent,
      removeItemsFromCartStore,
      errorMessages.removeLine,
      cart.items,
      aggregateThisAndRelatedEmbroideryItems,
      aggregateThisAndRelatedPendingEmbroideryLogos,
      getLogoSetupItem,
      finalSale,
      getNewKitId,
      aggregateRestOfKitsNotRelatedToEmbroideryItems,
    ]
  )

  const updateItemsQuantity = useCallback<UseCartOutput['updateItemsQuantity']>(
    async (keys, calculateQuantity) => {
      const itemsToUpdate: Map<string, { item: CartItem; newQuantity: number }> = new Map<
        string,
        { item: CartItem; newQuantity: number }
      >()
      const itemsToRemove: string[] = []

      keys.forEach(key => {
        const itemToUpdate = cart.items[key]
        if (!itemToUpdate) return

        const newQuantity =
          typeof calculateQuantity === 'function'
            ? calculateQuantity(itemToUpdate.quantity)
            : itemToUpdate.quantity + calculateQuantity

        if (newQuantity <= 0) {
          itemsToRemove.push(key)
          return
        }

        if (isEmbroiderableProduct(itemToUpdate)) {
          const thisAndRelatedEmbroideryItems = aggregateThisAndRelatedEmbroideryItems(
            itemToUpdate.properties._guid,
            cart.items
          )
          thisAndRelatedEmbroideryItems.forEach(item => {
            itemsToUpdate.set(item.key, { item, newQuantity })
          })
        } else {
          itemsToUpdate.set(itemToUpdate.key, { item: itemToUpdate, newQuantity })
        }
      })

      if (itemsToRemove.length > 0) {
        removeItems(itemsToRemove)
      }

      if (itemsToUpdate.size > 0) {
        await setItemQuantitiesInCartStore([...itemsToUpdate.values()])
      }
    },
    [aggregateThisAndRelatedEmbroideryItems, cart.items, removeItems, setItemQuantitiesInCartStore]
  )

  const proceedToCheckout = useCallback<UseCartOutput['proceedToCheckout']>(
    async options => {
      const callbackBeforeRedirect = options?.callbackBeforeRedirect
      const providedDiscountCode = options?.discountCode

      const checkoutQueryParams: Record<string, string> = {}
      const finalDiscountCode = providedDiscountCode ?? discountCookieVal
      if (finalDiscountCode) checkoutQueryParams['discount'] = finalDiscountCode
      if (facebookCookieVal) checkoutQueryParams['FBCLID'] = facebookCookieVal

      try {
        const redirectUri = await retrieveCheckoutUrl(checkoutQueryParams)

        if (!redirectUri) {
          reportClientError({
            error: new Error('No checkout/redirect URI found.'),
            context: { scope: 'useCart', label: 'proceedToCheckout' },
          })
          return
        }

        if (callbackBeforeRedirect) await callbackBeforeRedirect()

        reportClientEvent({
          label: 'navigated-to-checkout',
          context: {
            scope: 'useCart',
            itemCount: `${cart.itemCount}`,
            cartValue: `${cart.totalPrice}`,
          },
        })

        trackProceedToCheckout({
          shopifyCartId: cartStoreCart.id!,
          shopifyCheckoutURL: redirectUri,
        })

        setErrorMessage(undefined)
        window.location.assign(redirectUri)
      } catch (error) {
        setErrorMessage(errorMessages.proceedToCheckout)
        reportClientError({
          error: new Error('Error occurred creating/proceeding to checkout.'),
          context: { scope: 'useCart', label: 'proceedToCheckout' },
        })

        // FIXME: this should be refactored as part of FIGS-5648
        // throwing error to maintain expected behavior in `MiniCartContent`
        throw error
      }
    },
    [
      discountCookieVal,
      facebookCookieVal,
      cart.itemCount,
      cart.totalPrice,
      trackProceedToCheckout,
      cartStoreCart.id,
      retrieveCheckoutUrl,
      errorMessages.proceedToCheckout,
    ]
  )

  return {
    cart,
    status,
    lastAddedItems,
    errorMessage,
    addGlobalAttributes,
    addItems,
    removeItems,
    updateItemsQuantity,
    proceedToCheckout,
  }
}

export const CartContainer = createContainer(useCart)
CartContainer.Provider.displayName = 'CartProviderImpl'
